package com.iregained.site.components.sections.promo.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.contact.sections.TimelineStepData
import com.iregained.site.components.sections.contact.styles.TimelineNumberCircleStyle
import com.iregained.site.components.sections.contact.styles.TimelineStepDescStyle
import com.iregained.site.components.sections.contact.styles.TimelineStepRowStyle
import com.iregained.site.components.sections.contact.styles.TimelineStepTitleStyle
import com.iregained.site.components.sections.promo.styles.PromoContainerStyle
import com.iregained.site.components.sections.promo.styles.PromoGridStyle
import com.iregained.site.components.sections.promo.styles.PromoIframeContainerStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Iframe
import org.jetbrains.compose.web.dom.Text

// ==========================================
// UNIQUE ANIMATION: Slide Up Fade
// ==========================================
@Composable
private fun Modifier.slideUp(isVisible: Boolean, delayMs: Int = 0): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 40.px).transition(
        Transition.of("opacity", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

// ==========================================
// CMS DATA MODEL
// ==========================================
data class PromoBookingSectionData(
    val title: String = "Claim Your Free Month",
    val description: String = "We know integrating physical MyHand® System into a busy clinic is a major decision. Our evaluation process is designed to respect your time and ensure a perfect fit. \n\nBook your clinical demonstration today. Secure your slot by July 31st to get your first month of the MyHand® System entirely free.",
    val timelineSteps: List<TimelineStepData> = listOf(
        TimelineStepData(
            title = "Select a Time", description = "Use the calendar to pick a date and time slot that works for your team."
        ), TimelineStepData(
            title = "Virtual Demo",
            description = "We'll walk you through the MyHand® System and answer any questions."
        ),
        TimelineStepData(
            title = "Claim Your Offer",
            description = "Sign-up for the program and acquire the system and your first month is on us—saving you up to $999 CAD."
        ), TimelineStepData(
            title = "On-Site Trial & Deployment",
            description = "Experience plug-and-play shipping, installation, and comprehensive staff training directly in your facility."
        )
    ),
    val calendarIframeUrl: String = "https://calendar.google.com/calendar/appointments/schedules/AcZssZ3VxjlczznUJ1m1PGHYSHSpsXx1n3iB0p9eT7gZHNupWHRXPP_6m3VMPRnlFfM-mMSDGlOYbqT9?gv=true"
)

val defaultPromoBookingData = PromoBookingSectionData()

@Composable
fun PromoBookingSection(data: PromoBookingSectionData = defaultPromoBookingData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    var isVisible by remember { mutableStateOf(false) }

    Column(
        modifier = PromoContainerStyle.toModifier().id("booking-calendar")
            .backgroundColor(palette.custom.lightGray) // Soft background to make the white calendar pop
            .padding(topBottom = if (isDesktop) 80.px else 40.px, leftRight = 5.percent),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Div(
            attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center).toAttrs {
                    ref { element ->
                        val observer = IntersectionObserver { entries, obs ->
                            entries.forEach {
                                if (it.isIntersecting) {
                                    isVisible = true
                                    obs.unobserve(element)
                                }
                            }
                        }
                        observer.observe(element)
                        onDispose { observer.disconnect() }
                    }
                }) {
            SimpleGrid(
                numColumns(1, lg = 2), modifier = PromoGridStyle.toModifier()
            ) {
                // LEFT COLUMN: Value Proposition & Timeline (Now vertically centered)
                Column(
                    modifier = Modifier.fillMaxWidth()
                        .height(100.percent) // Forces the column to match the iframe's height
                        .display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                        .padding(right = if (isDesktop) 40.px else 0.px)
                ) {

                    Column(modifier = Modifier.slideUp(isVisible, 0)) {
                        SpanText(
                            text = data.title,
                            modifier = Modifier.fontSize(if (isDesktop) 48.px else 36.px).fontWeight(FontWeight.Bold)
                                .color(palette.custom.darkText).lineHeight(1.2).margin(bottom = 16.px)
                        )

                        SpanText(
                            text = data.description,
                            modifier = Modifier.fontSize(18.px).lineHeight(1.5).color(palette.custom.darkText)
                                .whiteSpace(
                                    WhiteSpace.PreWrap
                                ).margin(bottom = 40.px)
                        )
                    }

                    data.timelineSteps.forEachIndexed { index, step ->
                        key(step.title) {
                            Row(
                                modifier = TimelineStepRowStyle.toModifier().margin(bottom = 24.px)
                                    .slideUp(isVisible, 150 + (index * 150)), verticalAlignment = Alignment.Top
                            ) {
                                Box(
                                    modifier = TimelineNumberCircleStyle.toModifier(),
                                    contentAlignment = Alignment.Center
                                ) { Text("${index + 1}") }

                                Column {
                                    SpanText(
                                        text = step.title, modifier = TimelineStepTitleStyle.toModifier()
                                    )
                                    Div(
                                        attrs = TimelineStepDescStyle.toModifier().toAttrs {
                                            ref { htmlElement ->
                                                htmlElement.innerHTML = step.description
                                                onDispose {}
                                            }
                                        })
                                }
                            }
                        }
                    }
                }

                // RIGHT COLUMN: The Interactive Google Calendar
                Box(
                    modifier = PromoIframeContainerStyle.toModifier()
                        .borderTop(8.px, LineStyle.Solid, palette.brand.primary) // Dynamic brand color
                        .slideUp(isVisible, 300)
                ) {
                    Iframe(
                        // Google Calendar Appointment pages switch to a stacked vertical layout on mobile,
                        // which requires significantly more height to avoid double-scrollbars and cut-offs.
                        attrs = Modifier
                            .fillMaxWidth()
                            .height(if (isDesktop) 750.px else 1050.px) // Responsive height increase for mobile stack
                            .border(0.px)
                            .toAttrs {
                                attr("src", data.calendarIframeUrl)
                                attr("frameborder", "0")
                                attr("scrolling", "yes") // Explicitly allow scrolling for smaller devices
                            }
                    )
                }
            }
        }
    }
}